import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import LineItemsEditor, { computeTotals } from "@/components/LineItemsEditor";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import { SupportingTendersEditor } from "@/components/SupportingTendersEditor";
import { ArrowLeft, FloppyDisk, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const today = () => new Date().toISOString().slice(0, 10);

export default function SalesInvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeCompanyId, companies, isGstCompany, dateFloor } = useAuth();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [availableAdvance, setAvailableAdvance] = useState(0);
  const [applyAdvance, setApplyAdvance] = useState(true);
  const [date, setDate] = useState(today());
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("unpaid");
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentReference, setPaymentReference] = useState("");
  // Multi-mode split payments — each split = {mode, reference, amount}.
  // Empty array = classic single-mode flow (legacy fields still drive it).
  const [paymentSplits, setPaymentSplits] = useState([]);
  const [notes, setNotes] = useState("");
  const [supportingTenders, setSupportingTenders] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    (async () => {
      const [cs, ps, sv] = await Promise.all([api.get("/customers"), api.get("/products"), api.get("/services").catch(() => ({ data: [] }))]);
      setCustomers(cs.data);
      const items = [
        ...ps.data.map((p) => ({ ...p, _kind: "product" })),
        ...(sv.data || []).map((s) => ({
          id: s.id, name: `${s.name} (Service)`, hsn: s.hsn_code || "",
          unit: "SRV", sale_price: s.price || 0, tax_rate: s.tax_rate ?? 18, _kind: "service",
        })),
      ];
      setProducts(items);
      if (isEdit) {
        try {
          const { data } = await api.get(`/sales-invoices/${id}`);
          setDate(data.date); setDueDate(data.due_date || "");
          setStatus(data.status); setPaidAmount(data.paid_amount || 0);
          setPaymentMode(data.payment_mode || "cash");
          setPaymentReference(data.payment_reference || "");
          setPaymentSplits(Array.isArray(data.payment_splits) ? data.payment_splits : []);
          setNotes(data.notes || ""); setSupportingTenders(data.supporting_tenders || []); setItems(data.items || []);
          const c = cs.data.find((x) => x.id === data.customer_id);
          setCustomer(c || { id: data.customer_id, name: data.customer_name, state_code: data.customer_state_code, gstin: data.customer_gstin });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
      }
    })();
  }, [activeCompanyId, id, isEdit]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const intraState = customer && activeCompany && customer.state_code && activeCompany.state_code && customer.state_code === activeCompany.state_code;
  const totals = useMemo(() => computeTotals(items, !!intraState, isGstCompany), [items, intraState, isGstCompany]);

  // Fetch on-account advance for the selected customer (only when creating a new invoice)
  useEffect(() => {
    if (isEdit || !customer?.id) { setAvailableAdvance(0); return; }
    api.get(`/ledger/customer/${customer.id}/available-advance`)
      .then((r) => setAvailableAdvance(Number(r.data.available_advance) || 0))
      .catch(() => setAvailableAdvance(0));
  }, [customer, isEdit]);

  const advanceToApply = Math.min(availableAdvance, totals.grand_total);

  const splitsTotal = useMemo(
    () => (paymentSplits || []).reduce((s, sp) => s + (Number(sp.amount) || 0), 0),
    [paymentSplits],
  );
  const effectivePaid = paymentSplits.length ? splitsTotal : Number(paidAmount) || 0;

  const save = async () => {
    if (!customer) { toast.error("Select a customer"); return; }
    if (items.length === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const cleanSplits = (paymentSplits || [])
        .map((sp) => ({
          mode: sp.mode || "cash",
          reference: (sp.reference || "").trim(),
          amount: Number(sp.amount) || 0,
          date: sp.date || date,
        }))
        .filter((sp) => sp.amount > 0);
      const payload = {
        date, due_date: dueDate, status,
        paid_amount: cleanSplits.length ? cleanSplits.reduce((s, sp) => s + sp.amount, 0) : (Number(paidAmount) || 0),
        payment_mode: cleanSplits.length ? cleanSplits[0].mode : paymentMode,
        payment_reference: cleanSplits.length ? cleanSplits[0].reference : paymentReference,
        payment_splits: cleanSplits,
        notes,
        supporting_tenders: (supportingTenders || []).map((s) => (s || "").trim()).filter(Boolean),
        customer_id: customer.id, customer_name: customer.name,
        customer_gstin: customer.gstin || "", customer_state_code: customer.state_code || "",
        items: items.map((it) => ({
          product_id: it.product_id || "", name: it.name, hsn: it.hsn || "",
          quantity: Number(it.quantity) || 0, unit: it.unit || "PCS",
          rate: Number(it.rate) || 0, discount_pct: Number(it.discount_pct) || 0,
          tax_rate: Number(it.tax_rate) || 0,
        })),
      };
      let res;
      if (isEdit) {
        res = await api.patch(`/sales-invoices/${id}`, payload);
        toast.success("Invoice updated");
      } else {
        const q = applyAdvance && advanceToApply > 0 ? "?apply_advance=true" : "";
        res = await api.post(`/sales-invoices${q}`, payload);
        if (applyAdvance && advanceToApply > 0) {
          toast.success(`Invoice created · ₹${advanceToApply.toLocaleString("en-IN")} advance auto-applied`);
        } else {
          toast.success("Invoice created");
        }
      }
      navigate("/sales");
      return res.data;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="page-shell text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="eyebrow">Sales</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{isEdit ? "Edit Invoice" : "New Sales Invoice"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
              <Link to={`/print/invoice/${id}`} target="_blank">
                <Button variant="outline" className="h-9"><Printer size={14} className="mr-1.5" /> Print</Button>
              </Link>
          )}
        </div>
      </div>

      <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">Customer *</Label>
          <SearchableCustomerSelect
            value={customer?.id || ""}
            onChange={(v) => setCustomer(customers.find((c) => c.id === v))}
            customers={customers}
            placeholder="Search & choose customer"
            testid="sale-customer"
            getSubtitle={(c) => c.gstin || c.phone}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Invoice Date</Label>
          <Input type="date" min={dateFloor} value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-4">
          <Label className="eyebrow">Notes / Work Name</Label>
          <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="md:col-span-4">
          <SupportingTendersEditor
            value={supportingTenders}
            onChange={setSupportingTenders}
            testIdPrefix="sale-supporting-tender"
          />
        </div>
      </div>

      {/* ── Mode of Payment: log every receipt (cash, UPI, RTGS…) — repeats allowed ── */}
      <div className="card-flat p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <Label className="eyebrow">Mode of Payment</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Log every payment against this invoice — the same mode may repeat (e.g. ₹500 Cash + ₹1,500 UPI + ₹2,000 RTGS + ₹1,000 UPI). The printed invoice shows deduplicated modes (e.g. <span className="font-mono">CASH · UPI · RTGS</span>).
            </p>
          </div>
          <div className="flex items-center gap-3">
            {paymentSplits.length > 0 && (
              <div className="text-[12px] text-muted-foreground">
                Auto Paid = <span className="font-mono font-semibold text-foreground">{inr(splitsTotal)}</span> · {paymentSplits.length} entr{paymentSplits.length > 1 ? "ies" : "y"}
              </div>
            )}
            <Button type="button" size="sm" variant="outline" className="h-8"
              onClick={() => setPaymentSplits([...(paymentSplits || []), { mode: "cash", reference: "", amount: 0 }])}
              data-testid="sale-add-split">
              + Add Payment
            </Button>
          </div>
        </div>
        {paymentSplits.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Paid Amount (₹)</Label>
              <Input type="number" step="0.01" value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                className="font-mono" data-testid="sale-paid" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Mode (Received)</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="sale-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="rtgs_neft">RTGS / NEFT</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Reference</Label>
              <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="UPI ID or Transaction ID / UTR No" className="font-mono" data-testid="sale-payment-ref" />
            </div>
          </div>
        )}
        {paymentSplits.length > 0 && (
          <div className="rounded border border-border overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-2.5 py-2 font-semibold w-[24%]">Mode</th>
                  <th className="text-right px-2.5 py-2 font-semibold w-[25%]">Amount (₹)</th>
                  <th className="text-left px-2.5 py-2 font-semibold w-[38%]">UPI ID or Transaction ID / UTR No</th>
                  <th className="px-1 py-2 w-[6%]"></th>
                </tr>
              </thead>
              <tbody>
                {paymentSplits.map((sp, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1.5">
                      <Select value={sp.mode || "cash"} onValueChange={(v) => setPaymentSplits(paymentSplits.map((x, idx) => idx === i ? { ...x, mode: v } : x))}>
                        <SelectTrigger className="h-8" data-testid={`sale-split-${i}-mode`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="rtgs_neft">RTGS / NEFT</SelectItem>
                          <SelectItem value="credit_card">Credit Card</SelectItem>
                          <SelectItem value="debit_card">Debit Card</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" step="0.01" min="0" value={sp.amount || 0}
                        onChange={(e) => setPaymentSplits(paymentSplits.map((x, idx) => idx === i ? { ...x, amount: Number(e.target.value) || 0 } : x))}
                        className="h-8 font-mono text-right" data-testid={`sale-split-${i}-amount`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input value={sp.reference || ""} onChange={(e) => setPaymentSplits(paymentSplits.map((x, idx) => idx === i ? { ...x, reference: e.target.value } : x))}
                        placeholder="UPI ID or Transaction ID / UTR No (optional)" className="h-8 font-mono" data-testid={`sale-split-${i}-ref`} />
                    </td>
                    <td className="px-1 py-1.5 text-right">
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                        onClick={() => setPaymentSplits(paymentSplits.filter((_, idx) => idx !== i))}
                        data-testid={`sale-split-${i}-remove`}>×</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/30">
                  <td className="px-2.5 py-2 text-right eyebrow">Total received</td>
                  <td className="px-2 py-2 text-right font-mono font-semibold" data-testid="sale-splits-total">{inr(splitsTotal)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {!isEdit && customer && availableAdvance > 0 && (
        <div className="card-flat p-4 flex items-center gap-3 border-l-4"
          style={{ borderLeftColor: "hsl(var(--success))" }}
          data-testid="advance-banner">
          <div className="flex-1">
            <div className="eyebrow" style={{ color: "hsl(var(--success))" }}>Advance on file</div>
            <div className="font-heading text-lg font-semibold mt-0.5">
              {customer.name} has {inr(availableAdvance)} in on-account balance
            </div>
            <div className="text-[12px] text-muted-foreground mt-0.5">
              {advanceToApply < availableAdvance
                ? `${inr(advanceToApply)} of it will settle this invoice · ${inr(availableAdvance - advanceToApply)} stays on account`
                : `Full advance will apply — remaining balance after invoice: ${inr(totals.grand_total - advanceToApply)}`}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={applyAdvance}
              onChange={(e) => setApplyAdvance(e.target.checked)}
              className="h-4 w-4"
              data-testid="apply-advance-toggle" />
            <span className="text-sm font-medium">Auto-apply advance</span>
          </label>
        </div>
      )}

      <LineItemsEditor items={items} setItems={setItems} products={products} intraState={!!intraState} gstEnabled={isGstCompany} />

      <div className={`grid grid-cols-1 ${isGstCompany ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
        {isGstCompany && (
          <div className="md:col-span-2 card-flat p-6">
            <div className="eyebrow mb-2">Tax Treatment</div>
            <div className="text-sm">
              {intraState ? "Intra-state · CGST + SGST" : "Inter-state · IGST"}
            </div>
            <div className="text-[12px] text-muted-foreground mt-1">
              Company: {activeCompany?.state_code || "—"} · Customer: {customer?.state_code || "—"}
            </div>
          </div>
        )}
        {!isGstCompany && (
          <div className="card-flat p-6">
            <div className="eyebrow mb-2">Non-GST Company</div>
            <div className="text-sm text-muted-foreground">This company has no GSTIN registered — invoices are generated without any GST calculation.</div>
          </div>
        )}
        <div className="card-flat p-6">
          <div className="eyebrow mb-3">Summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{isGstCompany ? "Taxable" : "Subtotal"}</span><span className="font-mono">{inr(totals.subtotal)}</span></div>
            {isGstCompany && (intraState ? (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="font-mono">{inr(totals.cgst_total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="font-mono">{inr(totals.sgst_total)}</span></div>
              </>
            ) : (
              <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="font-mono">{inr(totals.igst_total)}</span></div>
            ))}
            <div className="pt-2 border-t border-border flex justify-between font-heading font-bold text-base">
              <span>Grand Total</span><span className="font-mono" data-testid="sale-grand-total">{inr(totals.grand_total)}</span>
            </div>
            <div className="flex justify-between text-[12px]"><span className="text-muted-foreground">Paid</span><span className="font-mono">{inr(effectivePaid)}</span></div>
            <div className="flex justify-between font-medium"><span>Balance Due</span><span className="font-mono">{inr((totals.grand_total || 0) - effectivePaid)}</span></div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="h-11 px-6 text-base" data-testid="sale-save-btn">
          <FloppyDisk size={18} className="mr-1.5" /> {saving ? "Saving…" : "Save Invoice"}
        </Button>
      </div>
    </div>
  );
}
